import type { Option } from "@tento-lona/sheets";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { $qsa } from "@tento-ui/dom-selectors";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";

/** One column of a sheet, holding a cell per row. */
@component({
  name: "std-sheet-column",
})
export class SheetColumn extends LonaWebComponent {
  #placeholderKey: Option<string> = null;
  #presentationGeneration = 0;

  /** A queued presentation may target this column only within this lifetime. */
  get presentationGeneration(): number {
    return this.#presentationGeneration;
  }

  /** The column states in which a skeleton-painting column's bound content is stale. */
  static readonly observedAttributes: readonly string[] = Object.freeze([
    "column-placeholder",
    "pending-layout",
    "placed-stale",
  ]);
  #skeletonPainters = new Set<Element>();

  /**
   * Registers a descendant that paints this column's skeleton. It carries
   * `content-stale` exactly while this column is held, pending a rebind or
   * placed ahead of its bind, so the painter's own rule hides its stale
   * content and only the painter restyles (an inherited property set here
   * would restyle the column's whole subtree on every placement).
   */
  adoptSkeletonPainter(painter: Element): void {
    this.#skeletonPainters.add(painter);
    this.toggleAttribute("paints-skeleton", true);
    painter.toggleAttribute("content-stale", this.#contentStale());
  }

  /** Unregisters a painter that left this column; it keeps no stale mark. */
  releaseSkeletonPainter(painter: Element): void {
    this.#skeletonPainters.delete(painter);
    this.toggleAttribute("paints-skeleton", this.#skeletonPainters.size > 0);
    painter.removeAttribute("content-stale");
  }

  #poolListeners = new Set<{ readonly columnPooled: () => void }>();

  /**
   * Registers content to tell when this column goes back to the scroll pool
   * (`onRecycle`): it keeps its content for a later reattach at the same
   * index (`scroll-column-retention`), so it must stop reporting itself shown.
   */
  adoptPoolListener(listener: { readonly columnPooled: () => void }): void {
    this.#poolListeners.add(listener);
  }

  /** Unregisters content that left this column. */
  releasePoolListener(listener: { readonly columnPooled: () => void }): void {
    this.#poolListeners.delete(listener);
  }

  /**
   * Marks or clears every registered painter as this column enters or leaves a
   * stale state, and drops the placeholder's labels when the placeholder ends:
   * the bind that removes `column-placeholder` has already written the bound
   * labels in the same task, so the column never holds two sets of labels.
   */
  attributeChangedCallback(): void {
    const stale = this.#contentStale();
    for (const painter of this.#skeletonPainters) {
      if (painter.hasAttribute("content-stale") !== stale) painter.toggleAttribute("content-stale", stale);
    }
    if (!this.hasAttribute("column-placeholder") && this.#placeholderKey != null) {
      this.#placeholderKey = null;
      this.$("placeholder").replaceChildren();
    }
  }

  #contentStale(): boolean {
    return SheetColumn.observedAttributes.some((name) => this.hasAttribute(name));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    const generation = this.#presentationGeneration;
    // Sheet reconciliation can detach and reparent a retained column in one turn.
    queueMicrotask(() => {
      if (this.isConnected || generation !== this.#presentationGeneration) return;
      this.#presentationGeneration++;
    });
  }

  /** Shows `labels` (a placed header's day labels) in place of the column's stale content until its bind. */
  showPlaceholder(labels: string[]): void {
    const key = JSON.stringify(labels);
    if (key !== this.#placeholderKey) {
      this.#placeholderKey = key;
      const children: HTMLElement[] = [];
      if (labels.length > 0) {
        const header = document.createElement("div");
        header.className = "placeholder-dates";
        for (const label of labels) {
          const day = document.createElement("span");
          day.className = "text";
          day.textContent = label;
          header.appendChild(day);
        }
        children.push(header);
      }
      this.$("placeholder").replaceChildren(...children);
    }
    this.setAttribute("column-placeholder", "");
  }

  /** Windowed body cells preserve full semantic coordinates and scroll extent. */
  bindRowExtent(heightPx: Option<number>): void {
    const $cells = this.$("cells");
    $cells.style.setProperty("position", heightPx == null ? "" : "relative");
    $cells.style.setProperty("height", heightPx == null ? "" : `${heightPx}px`);
  }

  $cells(): SheetCell[] {
    return [...$qsa<SheetCell>("std-sheet-cell[slot=cells]", this)];
  }

  $customCells(): SheetCell[] {
    return [...$qsa<SheetCell>("std-sheet-cell[slot=custom]", this)];
  }

  /**
   * The column went back to the scroll pool. Its cells are reset for any
   * later index: no row identity, zero height, no placement, so the next bind
   * rebuilds every cell (below).
   *
   * `"park"` (`scroll-column-retention`): the pool keeps the column keyed by
   * the index it held and a fling back reattaches it there, so its cells keep
   * what their last bind wrote (`rid`, `rsid`, height, placement). A
   * reattach then reconciles every cell as kept (no
   * plugin recycle, no remount, no height or extent rewrite) and binds it in
   * place; a spare taken for another index still rebinds every cell, because
   * none displays that column. The recycle retires the
   * column's scene, so the reattach bind never takes the render skip the
   * `rid` note below guards against, and the kept heights are real ones. The
   * parked column is hidden by its park mark and is no rendered element, so
   * no sweep or geometry pass reads its cells.
   */
  onRecycle(how: "reset" | "park" = "reset") {
    for (const listener of this.#poolListeners) listener.columnPooled();
    this.#presentationGeneration++;
    this.removeAttribute("column-placeholder");
    if (how === "reset") this.bindRowExtent(null);
    SheetColumn.recycleCells(this.$cells(), how);
  }

  /** What a recycle does to the column's cells: nothing on `"park"`; on `"reset"` (see `onRecycle`) reset them. */
  static recycleCells($cells: readonly SheetCell[], how: "reset" | "park"): void {
    if (how === "park") return;
    for (const $cell of $cells) {
      for (const property of ["position", "top", "width"]) $cell.style.removeProperty(property);
      $cell.style.setProperty("--height", "0px");
      $cell.height = 0;
      // The zeroed height is only restored by a bind that calls `bindHeight` —
      // and the LR6 render-skip's eligibility rests on the documented invariant
      // that a recycled cell ALWAYS fully rebinds ("rid null or mismatched").
      // Keeping `rid` here violated that invariant: a pooled column returning
      // for the same date × same SYSTEM rows (`system(header)` is the same
      // identity on every sheet) would match rid + column + unchanged row
      // version and the skip would declare "unchanged output → no-op bind",
      // leaving cells bound-looking but 0px tall. Dropping `rid` makes the
      // recycled cell skip-ineligible, exactly as the guard assumes. (The
      // 2026-08-06 operator break itself rode the SIBLING zero path — columns
      // that SURVIVE a same-config sheet switch after
      // `scrubPreviousSheetVisualState` zeroed them, no recycle involved — so
      // the load-bearing fix is the height axis in the skip gates; this line
      // closes the recycle variant of the same broken contract.)
      $cell.removeAttribute("rid");
      // Its placement twin goes too, or the reconcile would map a pooled cell
      // back to a row by stable id alone.
      $cell.removeAttribute("rsid");
    }
  }

  replaceCustom($child: HTMLElement) {
    DomUtils.clearChildren(this.$("custom"));
    this.$("custom").appendChild($child);
  }

  toggleCellsVisibility(visible: boolean) {
    this.$("cells").style.setProperty("display", visible ? "block" : "none");
    this.$("custom").style.setProperty("display", visible ? "none" : "block");
  }

  toggleCustomVisibility(visible: boolean) {
    if (visible) {
      this.bindRowExtent(null);
      for (const $cell of this.$cells()) for (const property of ["position", "top", "width"]) $cell.style.removeProperty(property);
    }
    this.$("cells").style.setProperty("display", visible ? "none" : "block");
    this.$("custom").style.setProperty("display", visible ? "block" : "none");
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: flex;
        flex-direction: column;
        /* Each column is its own compositor layer. Measured (dom/perf T5): with
           columns flattened into the scroller's layer, Layerize grew from 84 to
           129 ms over a forward burst, so the layers stay. */
        will-change: transform;
        contain: layout;
      }

      #cells,
      #custom {
        /* Scroll placement belongs to the column host. Propagating its new
           offset through every cell makes a geometry-only resize restyle the
           entire calendar, competing with the sidebar animation. */
        --offset: initial;
        --idx: initial;
        --base-offset: initial;
      }

      /* Keep inherited overrides on the small preview subtree. Toggling
         them on the host would restyle every event cell twice per bind. */
      #placeholder { display: none; position: absolute; inset: 0; visibility: visible; pointer-events: none; }
      :host([column-placeholder]) #placeholder { display: block; }
      /* A header column placed before its first bind has no cells, so no
         height of its own: its label spans the header band the scroller
         measured (--body-top-offset), not the column's zero-height box. The
         placeholder is absolute, so the column's own height is unchanged. */
      :host([slot=header]) #placeholder { min-height: var(--body-top-offset, 0px); }
      :host([column-placeholder]:not([paints-skeleton])) #cells,
      :host([column-placeholder]:not([paints-skeleton])) #custom { clip-path: inset(100%); }
      /* A column that paints its own skeleton stays visible while placed or
         waiting for a rebind. Only its stale content is clipped, by the
         painter's own rule on the content-stale mark (adoptSkeletonPainter);
         nothing is set here for it to inherit. */
      .placeholder-dates { display: flex; height: 100%; align-items: center; }
      .placeholder-dates span { flex: 1; text-align: center; --font-size: var(--text-8px); color: var(--text-sec); }
    `,
  ];

  static $html: HTMLTemplateElement = html`
    <div id=placeholder aria-hidden=true></div>
    <div id=cells><slot name=cells></slot></div>
    <div id=custom><slot name=custom></slot></div>
  `;
}
